<template>
  <div class="w-full flex justify-center items-start min-h-screen bg-gray-50 pt-8 pb-24 px-4 sm:px-8">
    <vx-card class="w-full max-w-4xl shadow-lg border border-gray-100 p-6 md:p-10 mx-auto text-left">
      
      <!-- No Gap Confirmation Popup -->
      <vs-popup :title="$t('skillDetails.action2.a19')" :active.sync="confirmationNoGap">
        <div class="p-4 text-lg text-gray-700 text-left">
          {{$t('skillDetails.action2.a20')}}
        </div>
        <div class="flex justify-start mt-6 pl-4">
          <vs-button @click="close" :color="themeColor" type="filled">Ok, Got it</vs-button>
        </div>
      </vs-popup>

      <!-- ========================================== -->
      <!-- STEP 0: WELCOME & INSTRUCTIONS SCREEN      -->
      <!-- ========================================== -->
      <div v-if="responseStep === 0" class="fade-in text-left">
        
        <!-- Benchmark Mail Type -->
        <div v-if="$route.params.mailType === 'benchmark'" class="mb-3">
          <h2 class="text-3xl font-bold mb-8 text-dark text-left">
            {{ $t("endorsementInvite.response.r0") }} <span :style="{ color: themeColor }">{{ inviteUserName ? inviteUserName.toUpperCase() : '' }}</span>,
          </h2>
          <div class="text-lg text-gray-700 leading-relaxed space-y-6 text-left">
            <p>{{ $t("endorsementInvite.response.r1") }}</p>
            <p>{{ $t("endorsementInvite.response.r2") }}</p>
            <div class="p-6 rounded-lg border-l-4 bg-gray-100" :style="{ 'border-color': themeColor }">
              <p class="font-bold text-xl text-dark">
                {{ $t("endorsementInvite.response.r3") }}
              </p>
            </div>
            <p>{{ $t("endorsementInvite.response.r4") }}</p>
          </div>
        </div>

        <!-- Followup Mail Type -->
        <div v-if="$route.params.mailType === 'followup' || $route.params.mailType==='undefined'" class="mb-3">
          <h2 class="text-3xl font-bold mb-8 text-dark text-left">
            {{ $t("endorsementInvite.response.r0") }} <span :style="{ color: themeColor }">{{ inviteUserName ? inviteUserName.toUpperCase() : '' }}</span>,
          </h2>
          <div class="text-lg text-gray-700 leading-relaxed space-y-6 text-left">
            <p>{{ $t("endorsementInvite.response.r6") }}</p>
            <p>{{ $t("endorsementInvite.response.r7") }}</p>
            <p>{{ $t("endorsementInvite.response.r8") }}</p>
            <p>{{ $t("endorsementInvite.response.r9") }}</p>
          </div>
        </div>

        <!-- Signature integrated into the letter -->
        <div class="mt-12 pt-6 border-t border-gray-200 text-left">
          <p class="text-md text-gray-500 mb-1">Requested by</p>
          <p class="text-xl font-bold text-dark">{{ traineename ? traineename.toUpperCase() : '' }}</p>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- STEP 1+: QUESTIONNAIRE SCREENS             -->
      <!-- ========================================== -->
      <div v-if="isLoading === false && responseStep > 0 && responseSubmitted === false" class="fade-in text-left">
        <div v-for="(efoV, efoK) in externalFeedbackObj" :key="efoK">
          <div v-if="parseInt(responseStep) === parseInt(efoK) + 1">
            
            <!-- Prompt / Header -->
            <div class="mb-8 text-left">
              <p class="text-lg text-gray-600 mb-2">
                {{ $t("endorsementInvite.response.r11") }} <span class="font-bold text-dark">{{ traineename ? traineename.toUpperCase() : '' }}</span> {{ $t("endorsementInvite.response.r22") }}
              </p>
              
              <!-- The actual progress measure (Prominent) -->
              <div class="text-2xl md:text-3xl font-semibold leading-tight mt-4 p-6 rounded-lg shadow-sm border-l-4 text-left bg-gray-50" 
                   :style="{ color: themeColor, 'border-color': themeColor }">
                "{{ efoV["pmMap"] }}"
              </div>
            </div>

            <!-- Slider Section -->
            <div class="mb-10 w-full text-left pt-4">
              <vs-slider
                :color="themeColor"
                step="2"
                max="10"
                v-model="pm[responseStep - 1]"
                @change="storeEndorsementFeedback(responseStep - 1)"
                class="my-8"
              />
              <SliderValue :responseStep="responseStep" class="text-left" />
            </div>

            <!-- Notes Section -->
            <div class="mt-12 bg-gray-50 p-6 rounded-lg border border-gray-200 text-left w-full">
              <h5 class="text-lg font-bold text-dark mb-4">Additional Feedback (Optional)</h5>
              <vs-textarea
                name="input-7-1"
                class="w-full text-lg custom-textarea"
                v-model="notesDescription[responseStep - 1]"
                placeholder="You may add subjective feedback or suggestions to help with a better analysis..."
                @change="storeEndorsementFeedback(responseStep - 1)"
                rows="4"
              ></vs-textarea>
            </div>

          </div>

          <!-- Comparison Chart (If feedback >= 2) -->
          <div v-if="parseInt(responseStep) === parseInt(efoK) + 1 && efoV.feedback.length >= 2" class="mt-12 p-6 bg-white border border-gray-200 shadow-sm rounded-lg text-left">
            <h3 class="text-xl font-bold text-dark mb-6 text-left">Your Previous Feedbacks compared to your first benchmark</h3>
            <EndorsementChart :endorsementData="efoV"/>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- SUCCESS / SUBMITTED SCREEN                 -->
      <!-- ========================================== -->
      <div v-else-if="responseSubmitted === true" class="fade-in text-left py-12">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
          <i class="feather icon-check text-3xl text-green-500"></i>
        </div>
        <h2 class="text-3xl font-bold text-dark mb-4 text-left">Thank You!</h2>
        
        <p class="text-xl text-gray-600 leading-relaxed max-w-2xl text-left">
          {{ $t("endorsementInvite.response.r13") }}
        </p>

        <!-- Next Survey Date Display -->
        <div v-if="nextSurveyDateFormatted" class="mt-8 pt-6 border-t border-gray-200 max-w-2xl">
          <p class="text-lg text-gray-500 flex items-center">
            <i class="feather icon-calendar mr-3 text-2xl"></i>
            <span>Next email will come to you on: <strong class="ml-1" :style="{ color: themeColor }">{{ nextSurveyDateFormatted }}</strong></span>
          </p>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- FOOTER / NAVIGATION BUTTONS                -->
      <!-- ========================================== -->
      <div class="mt-10" v-if="responseSubmitted === false">
        <vs-divider class="mb-6"></vs-divider>
        <div class="flex flex-wrap justify-start items-center">
            
            <vs-button
              v-if="responseStep === 0"
              size="large"
              class="w-full md:w-auto px-12 text-lg font-semibold shadow-md rounded-lg"
              :color="themeColor"
              type="filled"
              @click="responseStep = responseStep + 1"
            >
              Start
            </vs-button>

            <!-- Disables button until they interact with the slider -->
            <vs-button
              v-else-if="responseStep < noOfStatements"
              size="large"
              class="w-full md:w-auto px-12 text-lg font-semibold shadow-md rounded-lg"
              :color="themeColor"
              type="filled"
              @click="responseStep = responseStep + 1"
              :disabled="disabledSubmitButton"
            >
              Next Question
            </vs-button>

            <vs-button
              v-else-if="responseStep === noOfStatements"
              size="large"
              class="w-full md:w-auto px-12 text-lg font-semibold shadow-md rounded-lg"
              :color="themeColor"
              type="filled"
              @click="saveExternalFeedback"
              :disabled="disabledSubmitButton"
            >
              Submit Feedback
            </vs-button>
            
            <span v-if="responseStep > 0 && disabledSubmitButton" class="w-full md:w-auto mt-4 md:mt-0 md:ml-4 text-sm text-gray-500 italic">
               * Please move the slider to continue
            </span>
        </div>
      </div>

    </vx-card>
  </div>
</template>

<script>
import EndorsementChart from "../../../components/EndorsementResponseChart";
import SliderValue from './SliderValue'
import i18n from "@/i18n/i18n";

import baseData from "@/i18n/i18n-en-Data-DPM.js";           
import deepData from "@/i18n/i18n-en-deep-Data.js";       
import pciData from "@/i18n/i18n-en-pci-Data-Coach.js";         
import peopletreeData from "@/i18n/i18n-en-peopletree-Data.js"; 

export default {
  name: "Response",
  components: {SliderValue,EndorsementChart},
  data() {
    return {
      responseStep: 0,
      pm: [0],
      pm_text: ["SLIDE TO RATE", "SLIDE TO RATE", "SLIDE TO RATE"],
      progressMeasureSlider: [true, true, true],
      firstName: "",
      notesDescription:[],
      inviteUserName:null,
      external_tokens:[],
      progress_measures_list: [],
      traineename:null,
      companyName:null,
      InviteSkillName:null,
      externalFeedbackObj: {},
      externalFeedbackId:null,
      endorsementChartObj: {},
      endorsementNewObj: {},
      custom_progress_measures: [],
      terminology:null,
      tabClose:false,
      mergedCCD: {}, 
      isLoading: true,
      confirmationNoGap:false,
      noOfStatements: 0,
      responseSubmitted: false,
      
      dynamicThemeColor: '#079E9A',
      nextSurveyDateFormatted: null // Variable to store the formatted next date
    };
  },
  computed: {
    disabledSubmitButton() {
      return this.responseStep !== this.$store.state.endorsementInvite.externalFeedback.length;
    },
    themeColor() {
      return this.dynamicThemeColor;
    }
  },
  created() {
    const sources = [baseData, peopletreeData, pciData, deepData];
    let buildMergedData = {};
    for (const src of sources) {
       let rootData = null;
       if (src && src.addNewSkill && src.addNewSkill.data) rootData = src.addNewSkill.data;
       else if (src && src.default && src.default.addNewSkill && src.default.addNewSkill.data) rootData = src.default.addNewSkill.data;
       else if (src && src['sc']) rootData = src;
       else if (src && src.default && src.default['sc']) rootData = src.default;

       if (!rootData) continue;

       for (const key in rootData) {
           if (!buildMergedData[key]) buildMergedData[key] = { competencies: [] };
           if (rootData[key] && rootData[key].competencies) {
               for (const comp of rootData[key].competencies) {
                   const existingComp = buildMergedData[key].competencies.find(c => c.id === comp.id);
                   if (existingComp) {
                       if (comp.progressMeasures) existingComp.progressMeasures = JSON.parse(JSON.stringify(comp.progressMeasures));
                       if (comp.comp) existingComp.comp = comp.comp;
                   } else {
                       buildMergedData[key].competencies.push(JSON.parse(JSON.stringify(comp)));
                   }
               }
           }
       }
    }
    this.mergedCCD = buildMergedData;

    const params = this.$route.params;
    if(!window.$cookies.get('companyName') && this.$route.query.companyName){
      window.$cookies.set('companyName', this.$route.query.companyName, Infinity);
      window.location.reload();
    }

    const externalToken = params.externalToken;
    this.inviteUserName=this.$route.query.inviteename;
    const data ={
      name: this.$route.query.companyName || window.$cookies.get('companyName')
    }
    
    this.$store
        .dispatch("company/find",{query:data})
        .then(res1 => {
          if(res1.data && res1.data.length > 0) {
            const compData = res1.data[0];
            localStorage.setItem('themeColor', compData.themeColor);
            localStorage.setItem('secondaryColor', compData.secondaryColor);
            
            if(compData.themeColor) {
               this.dynamicThemeColor = compData.themeColor;
            }
          }
        })
        .catch(err1 => {});

    if(this.$route.query.lang==''){
      this.lang='en'
      this.$i18n.locale=this.lang;
      window.$cookies.set('locale', this.lang, Infinity);
    } else {
      this.lang=this.$route.query.lang;
      if(this.lang){
        this.$i18n.locale = this.lang;
        window.$cookies.set('locale', this.lang, Infinity);
      }else{
        this.$i18n.locale ='en';
        this.lang='en';
        window.$cookies.set('locale','en', Infinity);
      }
    }

    if(!this.$route.query.traineename || this.$route.query.traineename.length === 0){
      this.$store.dispatch("external-feedback/find", {query: {externalToken: externalToken}}).then(res => {
        this.$store.dispatch( "endorsementInvite/fetchPrgms", res.data[0].endorsementInvitePrgmsId).then(res => {
          this.$store.dispatch( "endorsementInvite/fetchInvitees", {query: {id: res.endorsementInviteId}}).then(res => {
            this.$store.dispatch( "addSkill/getCompetency", res.data[0].traineeCompetenciesId).then(res => {
              this.$store.dispatch( "userManagement/fetchUser", res.usersId).then(res => {
                this.firstName=res.firstName;
                this.lastName=res.lastName;
                this.traineename=this.firstName+' '+this.lastName;
              }).catch(err => {})
            }).catch(err => {})
          }).catch(err => {})
        }).catch(err => {})
      }).catch(err => {})
    }else{
      this.traineename=this.$route.query.traineename;
    }

    const query = { externalToken: externalToken };
    
    this.$store
        .dispatch("invitee-message-id/find", { query: query })
        .then(messageRes => {
          if (messageRes.data.length === 0) {
             this.$router.push('/user/auth/feedback-link-expire');
             return;
          }

          const feedbackQuery = { externalToken: externalToken };
    
    this.$store
      .dispatch("external-feedback/find", { query: feedbackQuery })
      .then(res1 => {
         // 1. Block if token doesn't exist
         if (res1.data.length === 0) {
            this.$router.push('/user/auth/feedback-link-expire');
            return;
         }

         // 2. Block if feedback was already submitted for this link
         if (res1.data[0].feedbackReceivedDate !== null) {
            this.$router.push('/user/auth/feedback-link-expire');
            return;
         }

         // 3. Load the questionnaire
         let ef = 0;
         let c = 0;
         for (ef in res1.data) {
           this.fetchPrgms(ef, res1);
           c++;
         }
         for (let p = 0; p < c; p++) {
           this.pm[p] = 0;
         }
         this.noOfStatements = c;

      }).catch(err => {
         this.$router.push('/user/auth/feedback-link-expire');
      });
        })
        .catch(err1 => {});
  },
  watch: {
    '$route' () {
      if (this.showBookmarkPagesDropdown) this.showBookmarkPagesDropdown = false
    }
  },
  methods: {
    onClose(event) {
      if (this.tabClose === false){
        event.preventDefault()
        event.returnValue = "Here you can "
      }
    },
    close(){
      this.confirmationNoGap=false;
    },
    fetchPrgms(ef, res1) {
      this.$store
          .dispatch("endorsementInvite/fetchPrgms", res1.data[ef].endorsementInvitePrgmsId)
          .then(res2 => {
            this.externalFeedbackObj[ef] = {};
            this.externalFeedbackObj[ef]["extId"] = res1.data[ef].id;
            this.externalFeedbackObj[ef]["pmId"] = res2.progressMeasureId;
            
            if(res2.progressMeasureId.includes('custom')) {
              this.externalFeedbackObj[ef]["endorsementInvitePrgmsId"] = res1.data[ef].endorsementInvitePrgmsId;
              this.$store.dispatch("trainee-competencies-prgms/find", {query: {progressMeasureId: res2.progressMeasureId}}).then(res => {
                this.externalFeedbackObj[ef]["pmMap"] = res.data[0].progressMeasure
              }).catch(err => {})
              this.$store
                  .dispatch("external-feedback/find",{query:{endorsementInvitePrgmsId:res1.data[ef].endorsementInvitePrgmsId,currentGap:{$ne:null}}})
                  .then(res3 => {
                    this.externalFeedbackObj[ef]["feedback"]=res3.data;
                  })
              if (Object.keys(this.externalFeedbackObj).length === res1.total) {
                this.isLoading = false;
              }
            } else {
              const charId = res2.progressMeasureId.split("-")[0];
              const compId = res2.progressMeasureId.split("-")[1];
              const pmId = res2.progressMeasureId.split("-")[2];
              const fullCompId = `${charId}-${compId}`;
              this.externalFeedbackObj[ef]["endorsementInvitePrgmsId"] = res1.data[ef].endorsementInvitePrgmsId;
              
              let pmMapText = "Progress Measure Text Unavailable";
              
              if (this.mergedCCD && this.mergedCCD[charId] && this.mergedCCD[charId].competencies) {
                  const compData = this.mergedCCD[charId].competencies.find(c => c.id === fullCompId);
                  if (compData && compData.progressMeasures && compData.progressMeasures[pmId]) {
                      pmMapText = compData.progressMeasures[pmId].pmMap || compData.progressMeasures[pmId].pm;
                  }
              }

              this.externalFeedbackObj[ef]["pmMap"] = pmMapText;

              this.$store
                  .dispatch("external-feedback/find",{query:{endorsementInvitePrgmsId:res1.data[ef].endorsementInvitePrgmsId,currentGap:{$ne:null}}})
                  .then(res3 => {
                    this.externalFeedbackObj[ef]["feedback"]=res3.data;
                  })
              if (Object.keys(this.externalFeedbackObj).length === res1.total) {
                this.isLoading = false;
              }
            }
          })
          .catch(err2 => {});
    },
    getPMText(index) {
      if (this.pm[index] === 0) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r17");
      } else if (this.pm[index] === 2) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r18");
      } else if (this.pm[index] === 4) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r19");
      } else if (this.pm[index] === 6) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r19");
      } else if (this.pm[index] === 8) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r20");
      } else if (this.pm[index] === 10) {
        this.pm_text[index] = this.$t("endorsementInvite.response.r21");
      }
    },
    storeEndorsementFeedback(index) {
      this.getPMText(index);
      const gapObj = {
        currentGap: this.pm[index],
        index,
        notesDescription:this.notesDescription[index],
        currentGapText: this.pm_text[index],
        extId: this.externalFeedbackObj[index].extId
      };
      this.$store
          .dispatch("endorsementInvite/addExternalFeedback", gapObj)
          .then(res => {
            if(this.pm[index]===0) {
              this.confirmationNoGap = true;
            }else{
              this.confirmationNoGap = false;
            }
          })
          .catch(err => {});
    },
    async saveExternalFeedback() {
      const gapFeed = this.$store.state.endorsementInvite.externalFeedback;
      this.tabClose = true;
      window.addEventListener('beforeunload', this.onClose);

      // Set Language & Variables
      this.lang = (!this.$route.query.lang || this.$route.query.lang === 'undefined') ? 'en' : this.$route.query.lang;
      this.$i18n.locale = this.lang;
      this.traineename = this.$route.query.traineename;

      // 1. Check if all gaps have reached 0
      let allGapsZero = true;
      for (let i = 0; i < gapFeed.length; i++) {
        if (gapFeed[i].currentGap > 0) {
          allGapsZero = false;
          break;
        }
      }

      try {
        // 2. Save current feedback to DB
        await this.$store.dispatch("endorsementInvite/saveExternalFeedback", gapFeed);
        this.responseSubmitted = true;

        // 3. IF GAPS REMAIN -> Schedule the NEXT email dynamically
        if (!allGapsZero) {
          
          // Get required User/Company data for the next email
          const currentFeedDb = await this.$store.dispatch("external-feedback/find", { query: { id: gapFeed[0].extId }});
          const prgmsId = currentFeedDb.data[0].endorsementInvitePrgmsId;
          const inviteePrgrms = await this.$store.dispatch("endorsement-invite-prgms/find", { query: { id: prgmsId }});
          const endorsementInvite = await this.$store.dispatch("endorsement-invite/find", { query: { id: inviteePrgrms.data[0].endorsementInviteId }});
          const traineeComps = await this.$store.dispatch("trainee-competencies/find"); 
          const loginUser = await this.$store.dispatch("users/find", { query: { id: endorsementInvite.data[0].userId }});

          // OLD COMPILER SAFE CHECKS (No optional chaining)
          let companyName = this.$route.query.companyName;
          if (loginUser.data[0] && loginUser.data[0].company && loginUser.data[0].company[0] && loginUser.data[0].company[0].name) {
            companyName = loginUser.data[0].company[0].name;
          }

          let traineeEmail = this.$route.query.traineeEmail !== 'undefined' ? this.$route.query.traineeEmail : loginUser.data[0].email;
          
          this.feedbackFrequency = 7; // Default to 7
          if (loginUser.data[0] && loginUser.data[0].company && loginUser.data[0].company[0] && loginUser.data[0].company[0].feedbackFrequency) {
            this.feedbackFrequency = loginUser.data[0].company[0].feedbackFrequency;
          }

          // ---> THE MAGIC MATH: Calculate EXACT next date from TODAY <---
          let dt = new Date();
          let feedbackSendDate = new Date(dt.getTime() + this.feedbackFrequency * 24 * 60 * 60 * 1000);
          
          this.nextSurveyDateFormatted = feedbackSendDate.toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric'
          });

          // Generate ONE new token for the next batch
          const uuid4 = require("uuid4");
          const newToken = uuid4();

          // 4. Create new blank rows in `external-feedback` for the NEXT round
          for (let j = 0; j < gapFeed.length; j++) {
            if (gapFeed[j].currentGap > 0) { // Only schedule again if they haven't mastered this skill
              const fbDb = await this.$store.dispatch("external-feedback/find", { query: { id: gapFeed[j].extId }});
              await this.$store.dispatch("external-feedback/create", {
                endorsementInvitePrgmsId: fbDb.data[0].endorsementInvitePrgmsId,
                feedbackSentDate: feedbackSendDate,
                emailStatus: 0, 
                externalToken: newToken,
                followupCount: 0,
                notesDescription: ""
              });
            }
          }

          // 5. Build the Skill Name
          if (inviteePrgrms.data[0].progressMeasureId.length <= 12) {
            const charId = inviteePrgrms.data[0].progressMeasureId.split("-")[0];
            const compId = inviteePrgrms.data[0].progressMeasureId.split("-")[1];
            let skillNameFallback = "Skill";
            
            // OLD COMPILER SAFE CHECK
            if (this.mergedCCD && this.mergedCCD[charId] && this.mergedCCD[charId].competencies) {
              const compData2 = this.mergedCCD[charId].competencies.find(c => c.id === `${charId}-${compId}`);
              if (compData2) skillNameFallback = compData2.comp || "Skill";
            }
            this.InviteSkillName = skillNameFallback;
          } else {
            this.InviteSkillName = endorsementInvite.data[0].traineeCompetenciesId;
          }

          // 6. Send payload to your FeathersJS Mandrill hook
          let extFeedObj = {
            traineeName: this.traineename,
            flag: "followup_invitation_sent",
            feedbackReceived: 'yes',
            mailType: "followup",
            inviteeEmail: endorsementInvite.data[0].inviteeEmail,
            inviteUserName: endorsementInvite.data[0].inviteUserName,
            trainingDuration: traineeComps.data[0].trainingDuration,
            skillName: this.InviteSkillName,
            companyName: companyName,
            externalToken: newToken, // Pass the new token so Mandrill knows it!
            traineeEmail: traineeEmail,
            emailStatus: 0,
            lang: this.lang,
            feedbackSentDate: feedbackSendDate // Pass the future date
          };

          // Trigger Mandrill!
          await this.$store.dispatch("endorsementInvite/sendEmails", extFeedObj);
        }
      } catch (err) {
        console.error("Error saving feedback or scheduling next email:", err);
      }
    }
  },
  mounted()  {
    const themeFromLocal = localStorage.getItem('themeColor');
    if (themeFromLocal) {
        this.dynamicThemeColor = themeFromLocal;
    }
    
    window.addEventListener('beforeunload', this.onClose);
  },
};
</script>

<style scoped>
/* Smooth fade-in animation for transitions between questions */
.fade-in {
  animation: fadeIn 0.4s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Base custom styles */
.custom-textarea >>> textarea {
  font-size: 1.1rem !important;
  padding: 1rem !important;
  line-height: 1.5;
  border-radius: 8px;
}

/* Fix mobile scaling */
@media (max-width: 767px) {
  .vx-card {
    border-radius: 0;
    border: none;
    box-shadow: none;
  }
}
</style>